// SplitsDisplay.jsx
import React, { useState } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import {
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaHourglassHalf,
  FaPauseCircle,
  FaCommentDots,
} from 'react-icons/fa';
import toast from 'react-hot-toast';

import { fetchWithHandling, API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import ChatModal from './ChatModal';
import OutputRenderer from './OutputRenderer';

const SplitsDisplay = ({
  baseId,
  splits,
  executionId,
  workflowId,
  workflowStatus,
}) => {
  return (
    <div className="mt-4 space-y-4">
      {splits.map((split, index) => (
        <SplitNode
          key={`${baseId}-split-${index}`}
          unique_index={`${baseId}#${index}`}
          split={split}
          executionId={executionId}
          workflowId={workflowId}
          workflowStatus={workflowStatus}
        />
      ))}
    </div>
  );
};

const SplitNode = ({
  unique_index,
  split,
  executionId,
  workflowId,
  workflowStatus,
}) => {
  const [open, setOpen] = useState(false);
  // Local state for tool approval decisions for this split.
  const [approvalDecisions, setApprovalDecisions] = useState({});
  const [loading, setLoading] = useState(false);

  const [isChatModalOpen, setIsChatModalOpen] = useState(false); // <-- Renamed for clarity

  const { userGoogleId } = DataState();

  // Destructure split properties.
  const {
    label,
    status,
    output,
    error,
    splits: nestedSplits,
    approvalNeededTools,
    chats,
  } = split;
  // Toggle split details if status is success or failed.
  const toggleOpen = () => {
    if (status === 'success' || status === 'failed') setOpen((prev) => !prev);
  };

  // Render an icon based on the split status.
  const renderStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaHourglassHalf className="text-yellow-500" />;
      case 'running':
        return <FaSpinner className="animate-spin text-[#5652CF]" />;
      case 'success':
        return <FaCheckCircle className="text-green-500" />;
      case 'failed':
        return <FaTimesCircle className="text-red-500" />;
      case 'approvalNeeded':
        return <FaExclamationCircle className="text-yellow-500" />;
      case 'skipped':
      case 'idle':
      default:
        return <FaPauseCircle className="text-gray-500" />;
    }
  };

  // Get text color for the status.
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-500';
      case 'running':
        return 'text-[#5652CF]';
      case 'success':
        return 'text-green-600';
      case 'failed':
        return 'text-red-500';
      case 'approvalNeeded':
        return 'text-yellow-500';
      case 'skipped':
      case 'idle':
      default:
        return 'text-gray-500';
    }
  };

  // ----------------- Tool Approval Handlers -----------------
  // Update decision for a given tool in this split.
  const handleApprovalDecision = (toolId, approved, toolArguments) => {
    setApprovalDecisions((prev) => ({
      ...prev,
      [toolId]: {
        arguments: toolArguments,
        approved,
        disapproved: !approved,
        disapprovalReason: approved
          ? ''
          : prev[toolId]?.disapprovalReason || '',
      },
    }));
  };

  // Update disapproval reason for a given tool.
  const handleDisapprovalReasonChange = (toolId, value) => {
    setApprovalDecisions((prev) => ({
      ...prev,
      [toolId]: {
        ...prev[toolId],
        disapprovalReason: value,
      },
    }));
  };

  // Validate that every tool approval decision is complete.
  const isApprovalReady = (approvalTools) => {
    return Object.entries(approvalTools).every(([toolId]) => {
      const decision = approvalDecisions[toolId];
      if (!decision) return false;
      if (decision.approved === true) return true;
      if (
        decision.approved === false &&
        decision.disapproved === true &&
        decision.disapprovalReason &&
        decision.disapprovalReason.trim() !== ''
      ) {
        return true;
      }
      return false;
    });
  };

  // Placeholder submit function for the split tool approvals.
  const submitApproval = async () => {
    setLoading(true);
    try {
      const encodedSplitId = encodeURIComponent(split.id);
      await fetchWithHandling(
        `${API_URL}/canvas/workflow/executions/${executionId}/nodes/${encodedSplitId}/approval-update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userGoogleId}`,
          },
          // The endpoint expects a key "approvalNeededEmails"
          body: JSON.stringify({ approvalNeededTools: approvalDecisions }),
        },
      );
      // Reset decisions on successful submission.
      setApprovalDecisions({});
      toast.success('Tool approval submitted successfully!');
    } catch (error) {
      toast.error(typeof error === 'string' ? error : JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // ------------------------ Render ------------------------
  return (
    <div className="rounded-md bg-gradient-to-r from-zinc-800 to-zinc-700 p-4">
      {/* Split Header */}
      {/* eslint-disable-next-line */}
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={toggleOpen}
      >
        <div className="flex items-center gap-3">
          {renderStatusIcon(status)}
          <span className="text-p-text text-sm font-semibold">
            {label || 'Split'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* UPDATED: Chat Toggle Button */}
          {Array.isArray(chats) && chats.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsChatModalOpen(true);
              }}
              className="rounded-full p-2 text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-white"
              title="View Conversation"
            >
              <FaCommentDots />
            </button>
          )}
          <span className={`text-sm font-medium ${getStatusColor(status)}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          {(status === 'success' || status === 'failed') && (
            <MdArrowDropDown
              className={`text-p-text transition-transform duration-300 ${open ? 'rotate-180' : 'rotate-0'}`}
            />
          )}
        </div>
      </div>

      {/* Output/Error Section */}
      {open && (status === 'success' || status === 'failed') && (
        // <div className="mt-4 p-4 border-t border-zinc-700 bg-zinc-800 text-p-text text-sm whitespace-pre-wrap">
        //   <div dangerouslySetInnerHTML={{ __html: output || error }} />
        // </div>
        <div className="text-p-text mt-4 rounded-md border-t border-zinc-700 bg-zinc-800 p-4 text-sm">
          {/* 2. Replace the old div with the new component */}
          <OutputRenderer content={output || error} />
        </div>
      )}

      {/* Tool Approval Section */}
      {status === 'approvalNeeded' &&
        approvalNeededTools &&
        Object.keys(approvalNeededTools).length > 0 && (
          <div className="mt-6 rounded-md border border-zinc-700 bg-zinc-900 p-6 shadow-lg">
            <h3 className="text-p-text mb-4 text-xl font-semibold">
              Tool Approval Required
            </h3>
            {Object.entries(approvalNeededTools).map(([toolId, toolData]) => {
              // Expect toolData to have a toolName and an "arguments" JSON string.
              let parsedArgs = {};
              try {
                parsedArgs = JSON.parse(toolData.arguments);
              } catch (err) {
                toast.error(`Failed to parse arguments for tool ${toolId}`);
              }
              const decision = approvalDecisions[toolId] || {};
              return (
                <div
                  key={toolId}
                  className="mb-5 rounded-lg border border-zinc-600 bg-gradient-to-br from-zinc-800 to-zinc-700 p-5 shadow-sm"
                >
                  <div className="mb-3">
                    <p className="text-p-text text-base font-bold">
                      {toolData.toolName}
                    </p>
                  </div>
                  <div className="mb-4">
                    <span className="text-p-text block text-sm font-medium">
                      Arguments:
                    </span>
                    <div className="text-p-text mt-1 whitespace-pre-wrap break-words rounded border border-zinc-600 bg-zinc-800  p-3 text-xs ">
                      {JSON.stringify(parsedArgs, null, 2)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() =>
                          handleApprovalDecision(
                            toolId,
                            true,
                            toolData.arguments,
                          )
                        }
                        className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                          decision.approved === true
                            ? 'bg-green-600 text-white'
                            : 'text-p-text bg-zinc-700 hover:bg-zinc-600'
                        }`}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() =>
                          handleApprovalDecision(
                            toolId,
                            false,
                            toolData.arguments,
                          )
                        }
                        className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                          decision.approved === false
                            ? 'bg-red-600 text-white'
                            : 'text-p-text bg-zinc-700 hover:bg-zinc-600'
                        }`}
                      >
                        Disapprove
                      </button>
                    </div>
                    {decision.approved === false && (
                      <input
                        type="text"
                        placeholder="Enter disapproval reason..."
                        value={decision.disapprovalReason || ''}
                        onChange={(e) =>
                          handleDisapprovalReasonChange(toolId, e.target.value)
                        }
                        className="text-p-text w-full flex-grow rounded-md border border-zinc-600 bg-zinc-800 px-3 py-2 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#5652CF] sm:w-auto"
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {isApprovalReady(approvalNeededTools) && (
              <div className="mt-4 text-right">
                <button
                  disabled={loading}
                  onClick={submitApproval}
                  className="rounded-md bg-blue-600 px-8 py-3 text-base font-medium text-white transition-colors hover:bg-blue-700"
                >
                  {loading ? 'Submitting...' : 'Submit Approval'}
                </button>
              </div>
            )}
          </div>
        )}

      {/* NEW: Conditionally render the ChatModal outside the split node's flow */}
      <ChatModal
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        nodeLabel={label || 'Split'}
        chats={chats}
        nodeId={split.id}
        workflowId={workflowId}
        executionId={executionId}
        userGoogleId={userGoogleId}
        status={status}
        workflowStatus={workflowStatus}
      />

      {/* Nested Splits */}
      {nestedSplits && nestedSplits.length > 0 && (
        <div className="mt-4">
          <SplitsDisplay baseId={split.id} splits={nestedSplits} />
        </div>
      )}
    </div>
  );
};

export default SplitsDisplay;
